import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { defineMessages, FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { FaSave, FaSearch, FaUndo } from 'react-icons/fa';

import intlShape from '@/shared/utils/intlShape';
import EmployeeFilterService from '@/shared/services/EmployeeFilterService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import ToastrService from '@/shared/services/ToastrService';
import OkCancelModal from './OkCancelModal';
import DynamicFilterDropdown from './DynamicFilterDropdown';

const messages = defineMessages({
  SaveSearchCriteria: 'Save search criteria',
  RenameSearchCriteria: 'Rename search criteria',
  DeleteSearchCriteria: 'Delete search criteria',
  SelectSearchCriteria: 'Select search criteria',
  ReplaceSearchCriteria: 'Replace search criteria',
  ResetFilters: 'Reset filters',
  SelectAll: 'Select all',
  // Same labels used in AngularJS modal
  SearchCriteriaNameInputTitle: 'SearchCriteriaNameInputTitle',
  SearchCriteriaNameInputMessage: 'SearchCriteriaNameInputMessage',
  SearchCriteriaNameInputPlaceholder: 'SearchCriteriaNameInputPlaceholder',
});

const filtersEquals = (filter1, filter2, ignoreFilters) => {
  const object1 = { ...filter1 };
  const object2 = { ...filter2 };
  (ignoreFilters || []).forEach(key => {
    delete object1[key];
    delete object2[key];
  });
  return JSON.stringify(object1) === JSON.stringify(object2);
};

class SavedFiltersButton extends Component {
  state = {
    savedFilters: [],
    selectedFilter: null,
    showNameModal: false,
    nameInput: '',
    modalFilter: null, // Used for modal actions
  };

  componentDidMount() {
    EmployeeFilterService.load().then(this.reloadSavedFilters);
  }

  componentDidUpdate(oldProps) {
    const { currentFilters, ignoreFilters } = this.props;
    if (!filtersEquals(currentFilters, oldProps.currentFilters, ignoreFilters)) {
      this.findSelectedFilter();
    }
  }

  selectFilter = e => {
    const filterId = e.currentTarget.id;
    const { savedFilters } = this.state;
    const { onFiltersChanged } = this.props;
    const filter = savedFilters.find(x => x.id === filterId);
    if (!filter || !filter.filter) return;
    this.setState({ selectedFilter: filter });
    onFiltersChanged({ ...filter.filter });
  };
  resetFilters = () => {
    const { onFiltersChanged, defaultFilters } = this.props;
    this.setState({ selectedFilter: null });
    onFiltersChanged({ ...defaultFilters });
  };

  reloadSavedFilters = () => {
    const { screen, defaultFilters } = this.props;
    const savedFilters = EmployeeFilterService.getScreenFilters(screen)
      .map(x => ({
        ...x,
        // Parse json and apply them to the default filters (in case there is a new filter)
        filter: { ...(defaultFilters || {}), ...(JSON.parse(x.json) || {}) },
      }))
      .sort(dynamicSort('name'));
    this.setState({
      savedFilters,
    });
    this.findSelectedFilter();
  };

  findSelectedFilter = () => {
    const { currentFilters, defaultFilters, ignoreFilters } = this.props;
    const isDefault = filtersEquals(currentFilters, defaultFilters, ignoreFilters);
    this.setState(({ selectedFilter, savedFilters }) => {
      let newFilter = selectedFilter;
      if (newFilter && !filtersEquals(currentFilters, newFilter.filter, ignoreFilters))
        newFilter = null;
      if (!newFilter && !isDefault)
        newFilter = savedFilters.find(x => filtersEquals(currentFilters, x.filter, ignoreFilters));
      if (newFilter) newFilter = savedFilters.find(x => x.id === newFilter.id) || newFilter;
      return { selectedFilter: newFilter };
    });
  };
  showSaveFilterModal = () => {
    const { currentFilters, screen } = this.props;
    this.setState({
      showNameModal: true,
      nameInput: '',
      modalFilter: {
        screen,
        name: '',
        json: JSON.stringify(currentFilters),
      },
    });
  };
  closeSaveFilterModal = () => {
    this.setState({ showNameModal: false, modalFilter: null });
  };

  showRenameFilterModal = (filter, event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!filter || !filter.id) return;
    this.setState({ showNameModal: true, nameInput: filter.name, modalFilter: filter });
  };
  saveFilter = () => {
    const { modalFilter, nameInput } = this.state;
    if (!modalFilter) return;
    if (!nameInput) return;
    const model = { ...modalFilter, name: nameInput };
    return EmployeeFilterService.setFilter(model).then(updatedFilter => {
      if (updatedFilter) {
        const { intl } = this.props;
        ToastrService.changesSaved(intl);
        this.reloadSavedFilters();
      }
      this.setState({ showNameModal: false, modalFilter: null });
      return updatedFilter;
    });
  };
  handleNameInputKeyDownEvent = e => {
    if (e.keyCode === 13) {
      this.saveFilter();
    } else if (e.keyCode === 27) {
      this.closeSaveFilterModal();
    }
  };

  replaceFilter = (filter, event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!filter || !filter.id) return;
    const { currentFilters, intl } = this.props;
    const model = {
      ...filter,
      json: JSON.stringify(currentFilters),
    };
    return EmployeeFilterService.setFilter(model).then(updatedFilter => {
      if (updatedFilter) {
        ToastrService.changesSaved(intl);

        this.reloadSavedFilters();
      }
      return updatedFilter;
    });
  };
  deleteFilter = (filter, event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!filter || !filter.id) return;
    // Clear filter if it is currently selected
    this.setState(({ selectedFilter }) => ({
      selectedFilter: selectedFilter && selectedFilter.id !== filter.id ? selectedFilter : null,
    }));

    EmployeeFilterService.deleteFilter(filter).then(() => {
      const { intl } = this.props;
      ToastrService.changesSaved(intl);
      this.reloadSavedFilters();
    });
  };

  render() {
    const { selectedFilter, savedFilters, showNameModal, nameInput } = this.state;
    const {
      intl,
      currentFilters,
      defaultFilters,
      ignoreFilters,
      dynamicFilters,
      onDynamicFiltersChanged,
      className,
    } = this.props;

    const hasSavedFilters = savedFilters && savedFilters.length > 0;

    const showReset = !filtersEquals(currentFilters, defaultFilters, ignoreFilters);

    const dynamicFiltersItems =
      dynamicFilters && dynamicFilters.length
        ? [
            {
              id: 'selectall',
              label: intl.formatMessage(messages.SelectAll),
              selected: !dynamicFilters.some(x => !x.selected),
            },
            ...dynamicFilters,
          ]
        : [];

    return (
      <div className={classnames('save-filters', className)}>
        {!hasSavedFilters && showReset && (
          <button type="button" className="btn btn-default" onClick={this.showSaveFilterModal}>
            <FaSave className="react-icon fa-fw margin5R" />
            <FormattedMessage {...messages.SaveSearchCriteria} />
          </button>
        )}
        {hasSavedFilters && (
          <div className="btn-group dropdown">
            <button
              type="button"
              className="btn btn-default dropdown-toggle"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <FaSearch
                className={classnames('react-icon margin5R', {
                  'text-success': !!selectedFilter,
                  'text-orange': showReset && !selectedFilter,
                })}
              />
              {selectedFilter && selectedFilter.name ? (
                selectedFilter.name
              ) : (
                <FormattedMessage {...messages.SelectSearchCriteria} />
              )}
              <span className="caret margin5L" />
            </button>
            <ul className="dropdown-menu">
              {showReset && !selectedFilter && (
                <li>
                  <a onClick={this.showSaveFilterModal}>
                    <FaSave className="react-icon fa-fw margin5R" />
                    <FormattedMessage {...messages.SaveSearchCriteria} />
                  </a>
                </li>
              )}
              {showReset && (
                <li>
                  <a onClick={this.resetFilters}>
                    <FaUndo className="react-icon fa-fw margin5R" />
                    <FormattedMessage {...messages.ResetFilters} />
                  </a>
                </li>
              )}

              {showReset && <li role="separator" className="divider" />}
              {hasSavedFilters &&
                savedFilters.map(filter => (
                  <li
                    key={filter.id}
                    className={classnames({
                      active: selectedFilter && selectedFilter.id === filter.id,
                    })}
                  >
                    <a onClick={this.selectFilter} id={filter.id}>
                      <span>{filter.name}</span>
                      {!selectedFilter && showReset && (
                        <i
                          className="fa fa-exchange clickable margin5L"
                          title={intl.formatMessage(messages.ReplaceSearchCriteria)}
                          onClick={e => this.replaceFilter(filter, e)}
                        />
                      )}
                      <i
                        className="fa fa-pencil clickable margin5L"
                        title={intl.formatMessage(messages.RenameSearchCriteria)}
                        onClick={e => this.showRenameFilterModal(filter, e)}
                      />
                      <i
                        className="fa fa-trash clickable margin5L"
                        title={intl.formatMessage(messages.DeleteSearchCriteria)}
                        onClick={e => this.deleteFilter(filter, e)}
                      />
                    </a>
                  </li>
                ))}
            </ul>
          </div>
        )}
        <DynamicFilterDropdown items={dynamicFiltersItems} onChange={onDynamicFiltersChanged} />
        {showReset && (
          <button type="button" className="btn btn-default btn-reset" onClick={this.resetFilters}>
            <FaUndo className="react-icon fa-fw margin5R" />
            <FormattedMessage {...messages.ResetFilters} />
          </button>
        )}
        {showNameModal && (
          <OkCancelModal
            title={<FormattedMessage {...messages.SearchCriteriaNameInputTitle} />}
            show
            onClose={this.closeSaveFilterModal}
            onSave={this.saveFilter}
            disabled={!nameInput}
            disableFullScreen
          >
            <div>
              <FormattedMessage {...messages.SearchCriteriaNameInputMessage} />
            </div>
            <div>
              <input
                type="text"
                value={nameInput}
                onChange={e => this.setState({ nameInput: e.target.value })}
                className="form-control"
                maxLength={50}
                autoFocus
                onKeyDown={this.handleNameInputKeyDownEvent}
                placeholder={intl.formatMessage(messages.SearchCriteriaNameInputPlaceholder)}
              />
            </div>
          </OkCancelModal>
        )}
      </div>
    );
  }
}

SavedFiltersButton.propTypes = {
  intl: intlShape.isRequired,
  screen: PropTypes.string.isRequired,
  currentFilters: PropTypes.shape({}),
  defaultFilters: PropTypes.shape({}).isRequired,
  dynamicFilters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      selected: PropTypes.bool,
    }),
  ),
  ignoreFilters: PropTypes.arrayOf(PropTypes.string),
  onFiltersChanged: PropTypes.func.isRequired,
  onDynamicFiltersChanged: PropTypes.func,
  className: PropTypes.string,
};
SavedFiltersButton.defaultProps = {
  currentFilters: {},
  ignoreFilters: [],
  dynamicFilters: [],
  onDynamicFiltersChanged: () => {},
  className: '',
};

export default injectIntl(SavedFiltersButton);
